import { getByPath, setByPath } from '../../../utils/objectPath';
import { getBlockStyle } from './styleUtils';
import Icon, { ICON_NAMES } from './commonIcon';
import EditableText from './EditableText';
import EditablePicker from './EditablePicker';
import EditableLinkPath from './EditableLinkPath';

const ICON_OPTIONS = ICON_NAMES.map((n) => ({ value: n, label: n }));

/**
 * Shared inline-WYSIWYG editing helpers — extracted from TemplateServicePage
 * so every editable page (service pages, About page, …) gets the exact same
 * text/icon/link editing, array add/remove, and section show/hide behavior
 * instead of a risky per-page copy-paste of ~150 lines of battle-tested logic.
 *
 * Usage inside a page component:
 *   const { T, IconPick, LinkPick, RemoveBtn, AddBtn, SectionToggle, showSection } =
 *     useEditablePage({ data, styles, onChange, isEditing });
 */
export function useEditablePage({ data, styles, onChange, isEditing }) {
  const updateData = (path, value) => onChange?.({ data: setByPath(data, path, value) });
  const updateStyles = (blockId, nextBlockStyles) => onChange?.({ styles: { ...styles, [blockId]: nextBlockStyles } });
  const sv = (blockId) => getBlockStyle(styles, blockId);

  const T = (blockId, path, value, as = 'div', className = '') => (
    <EditableText
      blockId={blockId}
      as={as}
      className={className}
      value={value}
      editable={isEditing}
      onChange={(v) => updateData(path, v)}
      blockStyles={sv(blockId)}
      onStylesChange={(next) => updateStyles(blockId, next)}
    />
  );

  /** Removes item `index` from the array at `path` (dotted, e.g. "faq.items"). Edit mode only. */
  const removeAt = (path, index) => {
    const arr = getByPath(data, path);
    if (!Array.isArray(arr)) return;
    updateData(path, arr.filter((_, i) => i !== index));
  };

  const RemoveBtn = ({ path, index, small = false, label = 'Remove item' }) =>
    isEditing ? (
      <button
        type="button"
        className={`svcpg-item-remove${small ? ' svcpg-item-remove--sm' : ''}`}
        aria-label={label}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          removeAt(path, index);
        }}
      >
        ×
      </button>
    ) : null;

  /** Appends a new item to the array at `path`. `template` may be a value or a (currentArray) => value factory. */
  const addAt = (path, template) => {
    const arr = getByPath(data, path) || [];
    const item = typeof template === 'function' ? template(arr) : template;
    updateData(path, [...arr, item]);
  };

  const AddBtn = ({ path, template, label = 'Add item' }) =>
    isEditing ? (
      <button
        type="button"
        className="svcpg-add-item"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          addAt(path, template);
        }}
      >
        + {label}
      </button>
    ) : null;

  /* ---- whole-section show/hide (public page skips hidden sections entirely) ---- */
  const hiddenSections = data.hiddenSections || [];
  const isSectionHidden = (key) => hiddenSections.includes(key);
  const toggleSection = (key) => {
    const next = isSectionHidden(key)
      ? hiddenSections.filter((k) => k !== key)
      : [...hiddenSections, key];
    updateData('hiddenSections', next);
  };
  const SectionToggle = ({ sectionKey, label }) => {
    if (!isEditing) return null;
    const hidden = isSectionHidden(sectionKey);
    return (
      <button
        type="button"
        className={`svcpg-section-toggle${hidden ? ' svcpg-section-toggle--hidden' : ''}`}
        onClick={() => toggleSection(sectionKey)}
      >
        {hidden ? `Show "${label}" section` : `Hide "${label}" section`}
      </button>
    );
  };
  /** Skip a section entirely on the public page when hidden; in edit mode keep it visible (dimmed) so it can be found and re-shown. */
  const showSection = (key) => isEditing || !isSectionHidden(key);
  const sectionHiddenAttr = (key) => (isEditing && isSectionHidden(key) ? 'true' : undefined);

  const IconPick = (blockId, path, value, size) => {
    const iconColor = sv(blockId)?.desktop?.color || null;
    const setIconColor = (c) => {
      const block = sv(blockId);
      updateStyles(blockId, { ...block, desktop: { ...(block?.desktop || {}), color: c } });
    };
    return (
      <EditablePicker
        editable={isEditing}
        value={value}
        options={ICON_OPTIONS}
        onChange={(v) => updateData(path, v)}
        renderOption={(opt) => <Icon name={opt.value} size={18} />}
        color={iconColor}
        onColorChange={setIconColor}
      >
        <Icon name={value} size={size} color={iconColor} />
      </EditablePicker>
    );
  };

  /** Wraps a link's arrow/icon so its `to` destination is editable (click the arrow). */
  const LinkPick = (path, value, children, label = 'Link') => (
    <EditableLinkPath editable={isEditing} value={value} onChange={(v) => updateData(path, v)} label={label}>
      {children}
    </EditableLinkPath>
  );

  return {
    updateData,
    updateStyles,
    sv,
    T,
    RemoveBtn,
    AddBtn,
    SectionToggle,
    showSection,
    isSectionHidden,
    sectionHiddenAttr,
    IconPick,
    LinkPick,
  };
}
